/**
 * Route tree of the client panel (TZ 08). Every screen is a lazily loaded chunk (NFR-02). Authenticated routes
 * resolve the session through /auth/me; a first login with a temporary password is forced to change it
 * (SEC-02); sections disabled by permissions open a page that says why (FR-RB-01).
 */
import { validateSettingsSearch } from '../search/settings_search';
import type { QueryClient } from '@tanstack/react-query';
import {
  type RouterHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { isApiError } from '../api/api_error';
import type { MeResponse } from '../api/api_types';
import type { ClientEndpoints } from '../api/client_endpoints';
import { queryKeys } from '../api/query_keys';
import { RootLayout } from '../layout/root_layout';
import { RouteErrorView, RouteNotFoundView, RoutePendingView } from '../layout/route_states';
import {
  validateAnalyticsSearch,
  validateComparisonSearch,
  validateLoginSearch,
  validateRankingSearch,
  validateTableSearch,
  validateTrendsSearch,
} from '../search/analytics_search';
import { validateControlSearch } from '../search/control_search';
import { isSectionVisible, type SectionKey } from '../session/permissions';

export interface RouterContext {
  queryClient: QueryClient;
  endpoints: ClientEndpoints;
}

async function loadSession(routerContext: RouterContext): Promise<MeResponse | null> {
  try {
    return await routerContext.queryClient.query({
      queryKey: queryKeys.session,
      queryFn: () => routerContext.endpoints.auth.me(),
      staleTime: 5 * 60_000,
    });
  } catch (sessionError) {
    if (isApiError(sessionError) && sessionError.isUnauthenticated) {
      return null;
    }
    throw sessionError;
  }
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: RouteNotFoundView,
  errorComponent: RouteErrorView,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: validateLoginSearch,
  component: lazyRouteComponent(() => import('../features/auth/login_page'), 'LoginPage'),
});

/** Owner decision 2026-10-01: self-service password reset through the user's linked Telegram (public route). */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('../features/auth/forgot_password_page'), 'ForgotPasswordPage'),
});

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/change-password',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context);
    if (session === null) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { session };
  },
  component: lazyRouteComponent(() => import('../features/auth/change_password_page'), 'ChangePasswordPage'),
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context);
    if (session === null) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    if (session.user.must_change_password) {
      throw redirect({ to: '/change-password' });
    }
    return { session };
  },
  component: lazyRouteComponent(() => import('../layout/client_layout'), 'ClientLayout'),
});

/** The same sign-in rules as the panel, without its sidebar and header: for screens that take the whole page. */
const fullPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_fullpage',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context);
    if (session === null) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    if (session.user.must_change_password) {
      throw redirect({ to: '/change-password' });
    }
    return { session };
  },
  component: lazyRouteComponent(() => import('../features/marketing/automation/full_page_layout'), 'FullPageLayout'),
});

const marketingAutomationNewRoute = createRoute({
  getParentRoute: () => fullPageRoute,
  path: '/marketing/automation/new',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_automation');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/automation/automation_wizard_pages'),
    'AutomationNewPage',
  ),
});

const marketingAutomationEditRoute = createRoute({
  getParentRoute: () => fullPageRoute,
  path: '/marketing/automation/$id',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_automation');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/automation/automation_wizard_pages'),
    'AutomationEditPage',
  ),
});

function requireSection(session: MeResponse, sectionKey: SectionKey): void {
  if (!isSectionVisible(session, sectionKey)) {
    // Store control is a separate product: a company without it never learns of the section (plan store_control_v1).
    if (sectionKey === 'store_control') {
      throw redirect({ to: '/' });
    }
    // A direct link or bookmark to a closed section says which one and why instead of a silent dashboard.
    throw redirect({ to: '/locked', search: { section: sectionKey } });
  }
}

/** The explanation page of a closed section (features/access). */
const sectionLockedRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/locked',
  validateSearch: (search: Record<string, unknown>): { section?: string } =>
    typeof search.section === 'string' ? { section: search.section } : {},
  component: lazyRouteComponent(() => import('../features/access/section_locked_page'), 'SectionLockedPage'),
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  validateSearch: validateAnalyticsSearch,
  component: lazyRouteComponent(() => import('../features/dashboard/dashboard_page'), 'DashboardPage'),
});

const storesIndexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/stores',
  beforeLoad: ({ context }) => {
    const firstStore = context.session.stores[0];
    if (firstStore === undefined) {
      throw redirect({ to: '/' });
    }
    throw redirect({ to: '/stores/$storeId', params: { storeId: firstStore.id } });
  },
});

const storeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/stores/$storeId',
  validateSearch: validateTableSearch,
  beforeLoad: ({ context, params }) => {
    if (!context.session.stores.some((store) => store.id === params.storeId)) {
      throw redirect({ to: '/stores' });
    }
  },
  component: lazyRouteComponent(() => import('../features/store/store_page'), 'StorePage'),
});

const comparisonRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/compare',
  validateSearch: validateComparisonSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'comparison');
  },
  component: lazyRouteComponent(() => import('../features/comparison/comparison_page'), 'ComparisonPage'),
});

const rankingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/ranking',
  validateSearch: validateRankingSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'ranking');
  },
  component: lazyRouteComponent(() => import('../features/ranking/ranking_page'), 'RankingPage'),
});

const trendsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/trends',
  validateSearch: validateTrendsSearch,
  component: lazyRouteComponent(() => import('../features/trends/trends_page'), 'TrendsPage'),
});

const marketingOverviewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_overview');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_overview_page'),
    'MarketingOverviewPage',
  ),
});

const marketingCampaignsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/campaigns',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_campaigns');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_campaigns_page'),
    'MarketingCampaignsPage',
  ),
});

const marketingContentRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/content',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_content');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_reports_pages'),
    'MarketingContentPage',
  ),
});

const marketingAdsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/ads',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_ads');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_reports_pages'),
    'MarketingAdsPage',
  ),
});

const marketingAutomationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/automation',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_automation');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_automation_page'),
    'MarketingAutomationPage',
  ),
});

const marketingSalesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/sales',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_sales');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_sales_page'),
    'MarketingSalesPage',
  ),
});

const marketingConnectionsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/connections',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_connections');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_connections_page'),
    'MarketingConnectionsPage',
  ),
});

const marketingReportRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/report',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_report');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_report_page'),
    'MarketingReportPage',
  ),
});

const marketingChatsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/chats',
  validateSearch: (search: Record<string, unknown>): { contact?: string } =>
    typeof search.contact === 'string' ? { contact: search.contact } : {},
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_chats');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_chats_page'),
    'MarketingChatsPage',
  ),
});

const marketingContactsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/marketing/contacts',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'marketing_contacts');
  },
  component: lazyRouteComponent(
    () => import('../features/marketing/marketing_chats_page'),
    'MarketingContactsPage',
  ),
});

const tradingHoursRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/trading-hours',
  validateSearch: validateTableSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'trading_hours');
  },
  component: lazyRouteComponent(
    () => import('../features/trading_hours/trading_hours_page'),
    'TradingHoursPage',
  ),
});

const qualityRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/quality',
  validateSearch: validateTableSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'quality');
  },
  component: lazyRouteComponent(() => import('../features/quality/quality_page'), 'QualityPage'),
});

const reportsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/reports',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'reports');
  },
  component: lazyRouteComponent(() => import('../features/reports/reports_page'), 'ReportsPage'),
});

const alertsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/alerts',
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'alerts');
  },
  component: lazyRouteComponent(() => import('../features/alerts/alerts_page'), 'AlertsPage'),
});

/** Store control ("Do'kon nazorati"): hidden and redirected when the company has no store control. */
const controlOverviewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control',
  validateSearch: validateControlSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'store_control');
  },
  component: lazyRouteComponent(
    () => import('../features/store_control/control_overview_page'),
    'ControlOverviewPage',
  ),
});

const controlEventsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control/events',
  validateSearch: validateControlSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'store_control');
  },
  component: lazyRouteComponent(
    () => import('../features/store_control/control_events_page'),
    'ControlEventsPage',
  ),
});

const controlCompareRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control/compare',
  validateSearch: validateControlSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'store_control');
  },
  component: lazyRouteComponent(
    () => import('../features/store_control/control_compare_page'),
    'ControlComparePage',
  ),
});

const controlServiceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/control/service',
  validateSearch: validateControlSearch,
  beforeLoad: ({ context }) => {
    requireSection(context.session, 'store_control');
  },
  component: lazyRouteComponent(
    () => import('../features/store_control/control_service_page'),
    'ControlServicePage',
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  validateSearch: validateSettingsSearch,
  component: lazyRouteComponent(() => import('../features/settings/settings_page'), 'SettingsPage'),
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  changePasswordRoute,
  fullPageRoute.addChildren([marketingAutomationNewRoute, marketingAutomationEditRoute]),
  authenticatedRoute.addChildren([
    dashboardRoute,
    storesIndexRoute,
    storeRoute,
    comparisonRoute,
    rankingRoute,
    trendsRoute,
    marketingReportRoute,
    marketingChatsRoute,
    marketingContactsRoute,
    marketingOverviewRoute,
    marketingCampaignsRoute,
    marketingContentRoute,
    marketingAdsRoute,
    marketingAutomationRoute,
    marketingSalesRoute,
    marketingConnectionsRoute,
    tradingHoursRoute,
    qualityRoute,
    reportsRoute,
    alertsRoute,
    controlOverviewRoute,
    controlEventsRoute,
    controlCompareRoute,
    controlServiceRoute,
    settingsRoute,
    sectionLockedRoute,
  ]),
]);

export function createAppRouter(routerContext: RouterContext, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: routerContext,
    ...(history === undefined ? {} : { history }),
    defaultPreload: 'intent',
    defaultPendingComponent: RoutePendingView,
    defaultPendingMs: 150,
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
